// MessageActionsMenu.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { toggleMessageStar } from "../../actions/message";

const getId = (value) => String(value?._id || value || "");

const VIEWPORT_GAP = 8;
const MENU_GAP = 6;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getMessageCopyText = (message) => {
    let text = String(message?.text || "");

    // Старый формат reply хранил служебную строку "↩️ Автор: цитата"
    // прямо в text. При копировании пользователю нужен только сам ответ.
    if (text.startsWith("↩️ ")) {
        const lineBreak = text.indexOf("\n");
        text = lineBreak >= 0 ? text.slice(lineBreak + 1) : "";
    }

    return text.trim();
};

const copyTextToClipboard = async (text) => {
    if (!text || typeof document === "undefined") return false;

    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_error) {
        // fallback ниже
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.select();

    try {
        const copied = document.execCommand("copy");
        return copied;
    } finally {
        document.body.removeChild(textarea);
    }
};

export default function MessageActionsMenu({
    open = false,
    message = null,
    isSelf = false,
    anchorPoint = null,
    onClose = null,
    onReply = null,
    onDelete = null,
    onForward = null,
    onSelect = null,
}) {
    const dispatch = useDispatch();
    const menuRef = useRef(null);

    const currentUserId = useSelector(
        (state) => state.auth?.user?._id || state.auth?.user?.id || null
    );

    const [menuPosition, setMenuPosition] = useState({
        top: 0,
        left: 0,
        placement: "down",
        ready: false,
    });

    const starred = Array.isArray(message?.starredBy) &&
        message.starredBy.some((id) => getId(id) === getId(currentUserId));

    const canForward = !!message?._id &&
        !String(message._id).startsWith("local:") &&
        message?.__sendStatus !== "sending" &&
        message?.__sendStatus !== "failed";
    const copyText = getMessageCopyText(message);
    const canCopyText = !!copyText;

    const positionMenu = useCallback(() => {
        const menu = menuRef.current;
        if (!menu || !anchorPoint || typeof window === "undefined") return;

        const menuWidth = menu.offsetWidth || 196;
        const menuHeight = menu.offsetHeight || 194;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const x = Number(anchorPoint.x || 0);
        const y = Number(anchorPoint.y || 0);
        const belowTop = y + MENU_GAP;
        const aboveTop = y - MENU_GAP - menuHeight;
        const hasRoomBelow = belowTop + menuHeight <= viewportHeight - VIEWPORT_GAP;
        const hasRoomAbove = aboveTop >= VIEWPORT_GAP;

        let placement = "down";
        let top = belowTop;

        if (!hasRoomBelow && hasRoomAbove) {
            placement = "up";
            top = aboveTop;
        } else if (!hasRoomBelow) {
            top = clamp(
                belowTop,
                VIEWPORT_GAP,
                Math.max(VIEWPORT_GAP, viewportHeight - menuHeight - VIEWPORT_GAP)
            );
        }

        const preferredLeft = isSelf ? x - menuWidth : x;
        const left = clamp(
            preferredLeft,
            VIEWPORT_GAP,
            Math.max(VIEWPORT_GAP, viewportWidth - menuWidth - VIEWPORT_GAP)
        );

        setMenuPosition({ top, left, placement, ready: true });
    }, [anchorPoint, isSelf]);

    useEffect(() => {
        if (!open || !message) return undefined;

        setMenuPosition((prev) => ({ ...prev, ready: false }));
        const frame = window.requestAnimationFrame(positionMenu);

        const handleOutside = (event) => {
            if (menuRef.current?.contains(event.target)) return;
            onClose?.();
        };

        const handleScroll = () => onClose?.();
        const handleResize = () => positionMenu();
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleResize);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleResize);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, message, onClose, positionMenu]);

    if (!open || !message || typeof document === "undefined") return null;

    const handleStar = async () => {
        await dispatch(toggleMessageStar(message._id, !starred));
        onClose?.();
    };

    const handleCopyText = async () => {
        if (!canCopyText) return;

        const copied = await copyTextToClipboard(copyText);
        if (copied) onClose?.();
    };

    const menuNode = (
        <div
            ref={menuRef}
            className={[
                "message-actions-AO__menu",
                "is-portal",
                `is-${menuPosition.placement}`,
                menuPosition.ready ? "is-ready" : "",
            ].filter(Boolean).join(" ")}
            style={{
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
            }}
            role="menu"
        >
            <button
                type="button"
                onClick={() => {
                    onReply?.();
                    onClose?.();
                }}
            >
                Ответить
            </button>

            <button
                type="button"
                disabled={!canCopyText}
                title={canCopyText ? "Скопировать текст сообщения" : "В сообщении нет текста"}
                onClick={handleCopyText}
            >
                Скопировать текст
            </button>

            <button type="button" onClick={handleStar}>
                {starred ? "★ Убрать из избранного" : "☆ В избранное"}
            </button>

            <button
                type="button"
                disabled={!canForward || !onForward}
                title={!canForward ? "Дождитесь отправки сообщения" : "Переслать"}
                onClick={() => {
                    if (!canForward) return;
                    onForward?.();
                    onClose?.();
                }}
            >
                Переслать
            </button>

            {!!onSelect && (
                <button
                    type="button"
                    disabled={!canForward}
                    onClick={() => {
                        if (!canForward) return;
                        onSelect?.();
                        onClose?.();
                    }}
                >
                    Выбрать
                </button>
            )}

            {isSelf && onDelete && (
                <button
                    type="button"
                    className="is-danger"
                    onClick={() => {
                        onDelete?.();
                        onClose?.();
                    }}
                >
                    Удалить
                </button>
            )}
        </div>
    );

    return createPortal(menuNode, document.body);
}
